import React, { useCallback, useEffect, useRef, useState } from 'react';
import { StyleSheet, View } from 'react-native';

import { NativeStackScreenProps } from '@react-navigation/native-stack';

import { LoadingSpinner } from '@/components/LoadingSpinner';
import { ds } from '@/components/lib/design.system';
import { PROD, getEnv } from '@/data';
import { useBotDetection } from '@/hooks/use-bot-detection';
import { ConfigFetchError, Product, useJurisdictionStore } from '@/hooks/use-jurisdiction';
import {
    CountryDetectError,
    CountyDetectError,
    NoLocationAccessError,
    actions as locationActions,
} from '@/hooks/use-location';
import { useResumeEffect } from '@/hooks/use-resume';
import { useUserStore } from '@/hooks/use-user-store';
import { handleKYCNavigation, useCoreNavigation } from '@/navigation/navigation';
import { RootStackParamList } from '@/navigation/types';
import { common } from '@/styles/styles';
import { E2E_TESTS_STATE } from '@/utils/constants';
import { isWeb } from '@/utils/constants-platform-specific';
import { readDeeplinkReferredProduct } from '@/utils/deeplink-referred-product';
import { logger } from '@/utils/logging';
import { setRootViewBackgroundColor } from '@/utils/set-root-view-background-color';

type ScreenProps = NativeStackScreenProps<RootStackParamList, 'Splash'>;

const styles = StyleSheet.create({
    root: {
        backgroundColor: ds.colorValue.background.brand.strong,
    },
});

const LOG_TAG = '[Bootstrap]';

const e2eStateParam = isWeb ? new URL(window.location.href).searchParams.get(E2E_TESTS_STATE) : null;

/**
 * Resolves the jurisdiction used for bots. Defaults to WI.
 * On non-production environments only, automated tests can override this via the `E2E_TESTS_STATE`
 * URL query param.
 */
const getBotJurisdiction = (): string => {
    if (getEnv() === PROD) {
        return 'WI';
    }

    return e2eStateParam ?? 'WI';
};

export function SplashScreen({ navigation, route }: ScreenProps) {
    const { openLaunch, showIneligibleStateError, showIneligibleCountry, openHome } = useCoreNavigation();
    const storedState = useJurisdictionStore(state => state.jurisdiction);
    const storedStateSource = useJurisdictionStore(state => state.jurisdictionSource);
    const { isBot, isLoading: isBotLoading } = useBotDetection();

    /**
     * If the user is on the web and has manually selected a US state before, we will use that state.
     * Otherwise this can causes some issues with redirects on web, after manually selecting a state.
     */
    const preSetManualState = storedStateSource === 'manual' && isWeb;
    const [manualUsState, setManualUsState] = useState<string | undefined>(preSetManualState ? storedState : undefined);
    const setJurisdictionAndUpdateSettings = useJurisdictionStore(
        store => store.actions.setJurisdictionAndUpdateSettings
    );
    const clearGpsData = useJurisdictionStore(store => store.actions.clearGpsData);
    const isInitRunning = useRef(false);

    const runInitSequence = useCallback(() => {
        if (isInitRunning.current) {
            return;
        }
        isInitRunning.current = true;

        const execute = async () => {
            try {
                if (isBotLoading) {
                    return;
                }

                //Load previous user session if any. This also loads the user profile/wallet.
                await useUserStore.getState().actions.loadPreviousSession();
                const isGuestUser = useUserStore.getState().guest;
                //If we are resuming a previous session,
                //re-check location, location access or default to the manual state if the user
                //just manually selected a US state
                const newUserState = isBot
                    ? getBotJurisdiction()
                    : manualUsState ?? (await locationActions.detectJurisdiction());
                const savedState = useJurisdictionStore.getState().jurisdiction;
                if (newUserState !== savedState) {
                    //State is different from the last detected state
                    logger.debug(LOG_TAG, `State changed from ${savedState} to ${newUserState}, must re-fetch configs`);
                }
                //Set jurisdiction and fetch new settings
                const settings = await setJurisdictionAndUpdateSettings(
                    newUserState,
                    manualUsState || isBot ? 'manual' : 'detected'
                );

                if (settings.productType === Product.None) {
                    showIneligibleStateError();
                    return;
                }
                const guestAllowed = settings.productConfig?.settings.guest_session_enabled ?? false;
                const { sessionHasExpired, removeGuestSession, setupGuestSession } = useUserStore.getState().actions;
                const isLoggedOut = sessionHasExpired();
                if (isLoggedOut) {
                    if (!guestAllowed) {
                        removeGuestSession();
                    } else {
                        setupGuestSession();
                    }
                }

                const canAccessProtectedRoutes = !isLoggedOut || guestAllowed;

                // redirect bot to the subpage they were trying to access
                if (isBot) {
                    if (!canAccessProtectedRoutes) {
                        openLaunch();
                        return;
                    }

                    const redirectPath = route.params?.redirectPath;
                    if (redirectPath) {
                        window.location.href = redirectPath;
                        return;
                    }
                }

                if (isGuestUser && guestAllowed) {
                    // If the user arrived via an AppsFlyer deep link, skip the
                    // gateless experience and send them to Sign Up / Log In.
                    const referredProduct = await readDeeplinkReferredProduct();
                    if (referredProduct) {
                        openLaunch();
                        return;
                    }
                    openHome();
                    return;
                }

                await handleKYCNavigation(navigation, useUserStore.getState().profile?.email);
            } catch (e) {
                logger.warn(LOG_TAG, e);
                clearGpsData();
                if (e instanceof ConfigFetchError) {
                    navigation.navigate('ErrorModal', {
                        title: 'An error occurred',
                        subtitle:
                            'We could not load the app settings. If the problem persists, contact support : support@betr.app',
                        showNavbarTitle: false,
                        primaryButton: 'Try Again',
                    });
                    return;
                }
                if (e instanceof NoLocationAccessError) {
                    navigation.navigate('AllowLocation', {
                        errorMode: 'locationAccess',
                        onSelectUsState: setManualUsState,
                    });
                    return;
                } else if (e instanceof CountyDetectError) {
                    navigation.navigate('AllowLocation', {
                        errorMode: 'countyDetect',
                        onSelectUsState: setManualUsState,
                    });
                    return;
                } else if (e instanceof CountryDetectError) {
                    showIneligibleCountry();
                    return;
                }

                openLaunch();
            } finally {
                isInitRunning.current = false;
            }
        };
        execute();
    }, [
        isBot,
        isBotLoading,
        manualUsState,
        setJurisdictionAndUpdateSettings,
        navigation,
        openLaunch,
        showIneligibleStateError,
        openHome,
        clearGpsData,
        showIneligibleCountry,
        route.params?.redirectPath,
    ]);

    useResumeEffect(runInitSequence);

    // keep the native root purple until the splash is gone, so there is no flash at launch
    useEffect(() => () => setRootViewBackgroundColor(ds.colorValue.background.neutral.base), []);

    return (
        <View style={[styles.root, common.paddingVertical, common.flex, common.justifyCenter]}>
            <LoadingSpinner />
        </View>
    );
}
